import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { AGENT_FLOW_COURSE_URL } from 'config/agentFlow';

import agentFlowIcon from 'assets/windowsIcons/agent-flow.png';
import back from 'assets/windowsIcons/back.png';
import forward from 'assets/windowsIcons/forward.png';
import refresh from 'assets/windowsIcons/refresh.png';
import home from 'assets/windowsIcons/home.png';
import earth from 'assets/windowsIcons/earth.png';

export default function AgentFlow() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const iframeRef = useRef(null);

  function handleLoad() {
    setLoading(false);
    setError(false);
  }
  function handleError() {
    setLoading(false);
    setError(true);
  }
  function reload() {
    setLoading(true);
    setError(false);
    if (iframeRef.current) {
      iframeRef.current.src = AGENT_FLOW_COURSE_URL;
    }
  }
  function goHome() {
    setLoading(true);
    setError(false);
    if (iframeRef.current) {
      iframeRef.current.src = AGENT_FLOW_COURSE_URL;
    }
  }

  return (
    <Div>
      {/* XP-style toolbar */}
      <section className="af__toolbar">
        <div className="af__toolbar__brand">
          <img src={agentFlowIcon} alt="AgentCraft" className="af__toolbar__icon-svg" />
          <span className="af__toolbar__title">AgentCraft</span>
          <span className="af__toolbar__sub">interactive course</span>
        </div>
        <div className="af__toolbar__buttons">
          <button className="af__toolbar__btn" onClick={goHome} title="Home">
            <img src={home} alt="home" className="af__toolbar__btn__img" />
          </button>
          <button className="af__toolbar__btn" onClick={reload} title="Refresh">
            <img src={refresh} alt="refresh" className="af__toolbar__btn__img" />
          </button>
        </div>
      </section>

      {/* Address bar */}
      <section className="af__address">
        <span className="af__address__label">Address</span>
        <div className="af__address__bar">
          <img src={earth} alt="" className="af__address__icon" />
          <span className="af__address__url">{AGENT_FLOW_COURSE_URL}</span>
        </div>
      </section>

      {/* Content area */}
      <div className="af__content">
        {loading && !error && (
          <LoadingOverlay>
            <div className="af__loading__logo">⟡</div>
            <div className="af__loading__title">AgentCraft</div>
            <div className="af__loading__sub">Loading interactive course...</div>
            <div className="af__loading__bar__track">
              <div className="af__loading__bar__fill" />
            </div>
          </LoadingOverlay>
        )}
        {error && (
          <ErrorOverlay>
            <div className="af__error__icon">⚠</div>
            <div className="af__error__title">Cannot display the course</div>
            <div className="af__error__msg">
              AgentCraft is not reachable at:<br />
              <code>{AGENT_FLOW_COURSE_URL}</code>
            </div>
            <div className="af__error__hint">
              Make sure <code>npm run dev</code> is running inside <code>agent-flow/</code>
            </div>
            <button className="af__error__btn" onClick={reload}>Try Again</button>
          </ErrorOverlay>
        )}
        <iframe
          ref={iframeRef}
          src={AGENT_FLOW_COURSE_URL}
          title="AgentCraft"
          className="af__iframe"
          onLoad={handleLoad}
          onError={handleError}
          allow="clipboard-write"
          style={{ opacity: loading || error ? 0 : 1 }}
        />
      </div>

      {/* XP status bar */}
      <footer className="af__footer">
        <div className="af__footer__status">
          <img src={earth} alt="" className="af__footer__icon" />
          <span>{loading ? 'Loading…' : error ? 'Page not found' : 'Done'}</span>
        </div>
        <div className="af__footer__zone">
          <img src={earth} alt="" className="af__footer__icon" />
          <span>Internet</span>
        </div>
      </footer>
    </Div>
  );
}

const barAnim = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #0f2044 0%, #1a3a6c 50%, #0a1628 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 10;
  font-family: Tahoma, sans-serif;
  color: #fff;

  .af__loading__logo {
    font-size: 48px;
    color: #7ab8f5;
    text-shadow: 0 0 24px rgba(122, 184, 245, 0.6);
  }
  .af__loading__title {
    font-size: 22px;
    font-weight: bold;
    color: #c8e4ff;
    letter-spacing: 2px;
  }
  .af__loading__sub {
    font-size: 11px;
    color: rgba(255,255,255,0.5);
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .af__loading__bar__track {
    width: 200px;
    height: 8px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 4px;
    overflow: hidden;
    margin-top: 8px;
  }
  .af__loading__bar__fill {
    width: 40%;
    height: 100%;
    background: linear-gradient(to right, #1a6ad0, #7ab8f5, #1a6ad0);
    border-radius: 4px;
    animation: ${barAnim} 1.2s infinite linear;
  }
`;

const ErrorOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  z-index: 10;
  font-family: Tahoma, sans-serif;
  padding: 24px;
  text-align: center;

  .af__error__icon {
    font-size: 36px;
    color: #c0392b;
  }
  .af__error__title {
    font-size: 16px;
    font-weight: bold;
    color: #333;
  }
  .af__error__msg {
    font-size: 11px;
    color: #555;
    line-height: 1.6;
    code { background: #f0f0f0; padding: 1px 4px; border-radius: 2px; font-size: 10px; }
  }
  .af__error__hint {
    font-size: 11px;
    color: #888;
    font-style: italic;
    code { background: #f0f0f0; padding: 1px 4px; border-radius: 2px; font-size: 10px; }
  }
  .af__error__btn {
    margin-top: 6px;
    padding: 4px 18px;
    font-family: Tahoma, sans-serif;
    font-size: 12px;
    background: linear-gradient(to bottom, #f5f5f5, #ddd);
    border: 1px solid #888;
    border-radius: 3px;
    cursor: pointer;
    &:hover { background: linear-gradient(to bottom, #fff, #e8e8e8); }
    &:active { background: #ccc; }
  }
`;

const Div = styled.div`
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: Tahoma, sans-serif;

  /* ── Toolbar ── */
  .af__toolbar {
    display: flex;
    align-items: center;
    height: 36px;
    flex-shrink: 0;
    background: linear-gradient(to bottom, #e8e0cc 0%, #d8cfb8 100%);
    border-bottom: 1px solid rgba(0,0,0,0.15);
    padding: 0 6px;
    gap: 8px;
  }
  .af__toolbar__brand {
    display: flex;
    align-items: baseline;
    gap: 6px;
    flex: 1;
  }
  .af__toolbar__icon-svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    border-radius: 3px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  }
  .af__toolbar__title {
    font-size: 14px;
    font-weight: bold;
    color: #1a1a1a;
    letter-spacing: 0.3px;
  }
  .af__toolbar__sub {
    font-size: 10px;
    color: rgba(0,0,0,0.45);
    letter-spacing: 0.5px;
    font-style: italic;
    text-transform: lowercase;
  }
  .af__toolbar__buttons {
    display: flex;
    gap: 2px;
  }
  .af__toolbar__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 26px;
    border: 1px solid transparent;
    background: transparent;
    border-radius: 3px;
    cursor: pointer;
    padding: 0;
    &:hover {
      background: linear-gradient(to bottom, rgba(255,255,255,0.6), rgba(255,255,255,0.2));
      border-color: rgba(0,0,0,0.2);
      box-shadow: inset 0 -1px 1px rgba(0,0,0,0.1);
    }
    &:active > * { transform: translate(1px, 1px); }
  }
  .af__toolbar__btn__img {
    width: 20px;
    height: 20px;
    pointer-events: none;
  }

  /* ── Address bar ── */
  .af__address {
    display: flex;
    align-items: center;
    height: 22px;
    flex-shrink: 0;
    background: linear-gradient(to right, #edede5 0%, #ede8cd 100%);
    border-bottom: 1px solid rgba(0,0,0,0.12);
    box-shadow: inset 0 -2px 3px -1px #2d2d2d22;
    padding: 0 4px;
    gap: 4px;
    font-size: 11px;
  }
  .af__address__label {
    color: rgba(0,0,0,0.45);
    white-space: nowrap;
    padding: 0 4px;
  }
  .af__address__bar {
    flex: 1;
    height: 16px;
    border: 1px solid rgba(122,122,255,0.5);
    background: #fff;
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 0 4px;
    overflow: hidden;
  }
  .af__address__icon {
    width: 13px;
    height: 13px;
    flex-shrink: 0;
  }
  .af__address__url {
    font-size: 10px;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Content (iframe) ── */
  .af__content {
    flex: 1;
    position: relative;
    overflow: hidden;
    border-left: 1px solid #6f6f6f;
    background: #fff;
  }
  .af__iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
    transition: opacity 0.3s ease;
  }

  /* ── Status bar ── */
  .af__footer {
    height: 20px;
    flex-shrink: 0;
    background: rgb(236,233,216);
    border-top: 1px solid transparent;
    box-shadow: inset 0 1px 3px rgba(50,50,50,0.5);
    display: flex;
    align-items: center;
    font-size: 11px;
    color: #333;
  }
  .af__footer__status {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 4px;
    padding-left: 4px;
  }
  .af__footer__zone {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 0 8px;
    border-left: 1px solid rgba(0,0,0,0.15);
    box-shadow: inset 1px 0 rgba(255,255,255,0.7);
    height: 80%;
  }
  .af__footer__icon {
    width: 13px;
    height: 13px;
  }
`;
